import {defineStore} from "pinia";
import {Favorite, FavPage, NavbarFavorite} from "@/features/dashboard/types/favorite.ts";
import {ref, Ref} from "vue";
import {getFavoriteTabs, updateFavorite} from "@/features/dashboard/services/favoriteService.ts";
import {PaginationRes} from "@/shared/types";

export const useNavbarFavoriteStore = defineStore('navbarFavorite', () => {
  const businessID: Ref<string> = ref<string>('');
  const userID: Ref<string> = ref<string>('');
  const favoriteID: Ref<string> = ref<string>('');
  const favorites: Ref<NavbarFavorite> = ref<NavbarFavorite>({
    pages: [],
    owners: [],
  } as NavbarFavorite);
  const loading = ref(false);
  const error: Ref<unknown> = ref(null);

  const loadFavorites = async () => {
    loading.value = true;
    error.value = null;
    try {
      const pg: PaginationRes = await getFavoriteTabs(businessID.value, userID.value);
      const data: Favorite[] = pg.data as Favorite[];
      let selected: Favorite = {} as Favorite;
      data.forEach((item: Favorite) => {
        selected = item;
      });

      favoriteID.value = selected.id;
      favorites.value = JSON.parse(selected.value) as NavbarFavorite;
    } catch (e) {
      error.value = e;
      throw e;
    } finally {
      loading.value = false;
    }
  };

  const addPage = (page: FavPage): void => {
    if (favorites.value.pages.find((pg: FavPage) => pg.route === page.route)) {
      throw new Error('Page already exists');
    }

    if (favorites.value.pages.length === 3) {
      favorites.value.pages.shift()
    }

    favorites.value.pages.push(page);
    saveFavorite();
  };

  const addOwner = (owner: FavPage): void => {
    if (favorites.value.owners.find((pg: FavPage) => pg.route === owner.route)) {
      throw new Error('Owner already exists');
    }

    if (favorites.value.owners.length === 3) {
      favorites.value.owners.shift()
    }

    favorites.value.owners.push(owner);
    saveFavorite();
  }

  const saveFavorite = async () => {
    if (!businessID.value || !userID.value || !favoriteID.value) return;

    try {
      await updateFavorite(businessID.value, userID.value, favoriteID.value, {
        value: JSON.stringify(favorites.value),
      } as Favorite);
    } catch (e) {
      error.value = e;
    }
  };

  const setBusinessUser = (business: string, user: string): void => {
    businessID.value = business;
    userID.value = user;
  }

  return {
    favorites,
    loading,
    error,
    loadFavorites,
    addPage,
    addOwner,
    setBusinessUser,
  };
});
